<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-console, no-use-before-define */
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import StorageMigration from './StorageMigration.vue';
import axios from 'axios';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
  folderStyleStatus: { type: String, required: false },
  storageMigrationStatus: { type: String, required: false },
  // Optional: effective bucket used for this analysis (provided by parent)
  bucket: { type: String, required: false },
  folderStyleEnabled: { type: Boolean, required: false, default: false },
  storageType: { type: String, required: false, default: 'from_account' },
});
const { t } = useI18n();
// downloadExistenceReport removed: unused helper (was causing a lint error)
const loadingExistence = ref(false);
const existenceResult = ref(null);
const showLastExecution = ref(false);
const isRefetchClicked = ref(false);
const existenceProgress = ref({
  status: 'not_started',
  processed: 0,
  total: 0,
});
// Percentual da barra de progresso (sem ".0" quando inteiro)
const percentProgress = computed(() => {
  const total = Number(existenceProgress.value.total) || 0;
  const processed = Number(existenceProgress.value.processed) || 0;
  if (!total) return '0% - 0 / 0';
  const percent = (processed / total) * 100;
  if (Number.isInteger(percent)) {
    return `${percent}% - ${processed} / ${total}`;
  }
  const percentStr = percent.toFixed(1).replace('.', ',');
  return `${percentStr}% - ${processed} / ${total}`;
});

// Button title for starting/refetching existence check
const existenceButtonTitle = computed(() => {
  if (existenceProgress.value && existenceProgress.value.status === 'running')
    return t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_DETAILED_FINISH');
  if (existenceProgress.value && existenceProgress.value.status === 'paused')
    return t('INBOX_MGMT.STORAGE_ANALYSIS.DETAILED_ANALYSIS_PAUSED');
  if (
    props &&
    props.folderStyleStatus &&
    ['running', 'paused'].includes(props.folderStyleStatus)
  ) {
    return props.folderStyleStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_FOLDER_REORG_FINISH')
      : t('INBOX_MGMT.STORAGE_ANALYSIS.FOLDER_REORG_PAUSED');
  }
  if (
    props &&
    props.storageMigrationStatus &&
    ['running', 'paused'].includes(props.storageMigrationStatus)
  ) {
    return props.storageMigrationStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_MIGRATION_FINISH')
      : t('INBOX_MGMT.STORAGE_ANALYSIS.MIGRATION_PAUSED');
  }
  return '';
});
const analysisDate = ref(null);
let progressInterval = null;

const startTime = ref(null); // Ensure startTime is defined
let startExistenceGuard = false;

const startExistenceCheck = async () => {
  loadingExistence.value = true;
  error.value = null;
  // mark that user clicked refetch when there was a previous result
  isRefetchClicked.value = !!existenceResult.value;
  // persist this flag immediately so it survives reloads/pauses
  try {
    await saveProgressToBackend();
  } catch (e) {
    /* non-blocking */
  }
  // Keep previous existenceResult so the button label remains 'Refazer...' when re-running.
  analysisDate.value = null;
  // Limpa estado local/caches imediatos
  clearInterval(progressInterval);
  clearInterval(elapsedTimeInterval);

  elapsedTime.value = '0s';
  existenceProgress.value = { status: 'not_started', processed: 0, total: 0 };
  // Ensure last-execution view is visible when user starts/re-runs the check
  showLastExecution.value = true;

  startTime.value = Date.now(); // Ensure startTime is set when the process starts

  // Start interval to update elapsed time (only if we actually start/poll below)

  try {
    // Check current backend state first to avoid double-starting (race/duplicate clicks)
    const { data: state } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/state`
    );
    // If backend is already running, start timer based on saved elapsed and poll
    if (state && state.status === 'running') {
      // restore elapsed time and start timer
      const elapsed = state.elapsed_time || '0s';
      elapsedTime.value = elapsed;
      let elapsedSeconds = 0;
      if (elapsed.includes('m')) {
        const match = elapsed.match(/(\d+)m\s*(\d+)s/);
        if (match) {
          elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        }
      } else {
        elapsedSeconds = parseInt(elapsed.replace('s', ''), 10) || 0;
      }
      startTime.value = Date.now() - elapsedSeconds * 1000;
      clearInterval(elapsedTimeInterval);
      elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
      pollExistenceProgress();
      return;
    }

    // If backend reports finished and zero total, treat as already done (avoid re-start)
    if (state && state.status === 'finished' && Number(state.total) === 0) {
      existenceProgress.value = state;
      // stop any running timer we started earlier
      clearInterval(elapsedTimeInterval);
      startTime.value = null;
      elapsedTime.value = state.elapsed_time || '0s';
      // reveal details since there's a last execution (UI will display based on state)
      // ensure UI shows final report (silent to avoid UI flicker)
      await fetchExistenceCheck(true);
      loadingExistence.value = false;
      return;
    }

    // Not already running: request start
    // Prevent duplicate starts by quickly flipping a guard
    if (startExistenceGuard) return;
    startExistenceGuard = true;
    try {
      await axios.post(
        `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/start`
      );
      // Immediately reflect running state locally so pause/stop controls show while polling
      existenceProgress.value = Object.assign({}, existenceProgress.value, {
        status: 'running',
        processed: 0,
        total: 0,
      });
      pollExistenceProgress();
      // start updating the elapsed time locally for a fresh run
      clearInterval(elapsedTimeInterval);
      elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
    } finally {
      startExistenceGuard = false;
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_START_EXISTENCE');
    loadingExistence.value = false;
    clearInterval(elapsedTimeInterval);
    isRefetchClicked.value = false;
  }
};

// removed last-execution toggles: details show automatically when appropriate

const saveProgressToBackend = async () => {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/save_progress`,
      {
        processed: existenceProgress.value.processed,
        total: existenceProgress.value.total,
        status: existenceProgress.value.status,
        elapsedTime: elapsedTime.value,
        // Persist whether this run was started via "Refazer" so we can restore UI after reload
        is_refetch: !!isRefetchClicked.value,
      }
    );
  } catch (e) {
    error.value =
      e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_SAVE_PROGRESS');
  }
};

const pollExistenceProgress = async () => {
  const update = async () => {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/progress`
    );
    existenceProgress.value = data;
    // Broadcast existence status so parent/other components can react (e.g., disable other controls)
    try {
      window.dispatchEvent(
        new CustomEvent('storage_existence:status', {
          detail: { inboxId: props.inboxId, status: data.status },
        })
      );
    } catch (e) {
      /* non-blocking */
    }
    // Restore persisted refetch flag if backend provided it
    if (typeof data.is_refetch !== 'undefined') {
      isRefetchClicked.value = !!data.is_refetch;
    }
    // Use updated_at from progress to show last change time on the progress bar
    if (data.updated_at) {
      analysisDate.value = new Date(data.updated_at).toLocaleDateString(
        'pt-BR'
      );
    }

    // Salva o progresso no backend periodicamente
    saveProgressToBackend();

    // Sempre atualiza o elapsedTime salvo no backend, inclusive ao recarregar concluído
    if (data.status === 'finished' && data.elapsed_time) {
      elapsedTime.value = data.elapsed_time;
    }

    // Edge case: backend reports zero total (no files). Treat as finished to avoid infinite running state.
    if (Number(data.total) === 0 && data.status !== 'finished') {
      try {
        existenceProgress.value.status = 'finished';
        if (!existenceProgress.value.elapsed_time)
          existenceProgress.value.elapsed_time = '0s';
        await saveProgressToBackend();
      } catch (e) {
        error.value =
          e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_SAVE_PROGRESS');
      }
      clearInterval(progressInterval);
      clearInterval(elapsedTimeInterval);
      await fetchExistenceCheck(true);
      loadingExistence.value = false;
      return;
    }

    if (data.status === 'finished') {
      // Salva o tempo decorrido final no backend
      await saveProgressToBackend();
      clearInterval(progressInterval);
      clearInterval(elapsedTimeInterval);
      await fetchExistenceCheck(true);
      loadingExistence.value = false;
      // keep isRefetchClicked true so labels persist after successful finish
    } else if (data.status === 'error') {
      // on error, clear state and reset the refetch flag
      await saveProgressToBackend();
      clearInterval(progressInterval);
      clearInterval(elapsedTimeInterval);
      await fetchExistenceCheck(true);
      loadingExistence.value = false;
      isRefetchClicked.value = false;
    }
  };
  await update();
  clearInterval(progressInterval);
  progressInterval = setInterval(update, 1000);
};

onUnmounted(() => {
  clearInterval(progressInterval);
  clearInterval(elapsedTimeInterval);
  clearInterval(baseAnalysisElapsedInterval);
});

const fetchExistenceCheck = async (silent = false) => {
  if (!silent) {
    loadingExistence.value = true;
    error.value = null;
    existenceResult.value = null;
  }
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}`
    );
    existenceResult.value = data;
    // Set analysis date: prefer backend timestamp if provided, otherwise use now
    const ts =
      data.generated_at ||
      data.generatedAt ||
      data.created_at ||
      data.createdAt ||
      data.updated_at ||
      data.updatedAt;
    analysisDate.value = ts
      ? new Date(ts).toLocaleDateString('pt-BR')
      : new Date().toLocaleDateString('pt-BR');
  } catch (e) {
    if (!silent) {
      error.value =
        e?.response?.data?.message ||
        t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_START_EXISTENCE');
    } else {
      error.value =
        e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_START_EXISTENCE');
    }
  } finally {
    if (!silent) loadingExistence.value = false;
  }
};

// Ao montar, verifica o estado em cache/redis. Se já estiver finalizado, carrega o relatório do cache.
onMounted(async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/state`
    );
    existenceProgress.value = data;

    // Broadcast initial existence status so other components can react on load
    try {
      window.dispatchEvent(
        new CustomEvent('storage_existence:status', {
          detail: { inboxId: props.inboxId, status: data.status },
        })
      );
    } catch (e) {
      /* non-blocking */
    }

    if (data && ['running', 'paused'].includes(data.status)) {
      // Auto-open panel when a run is in-progress or paused
      showLastExecution.value = true;
      loadingExistence.value = false;
      // restore elapsed time and timer
      const elapsed = data.elapsed_time || '0s';
      elapsedTime.value = elapsed;
      let elapsedSeconds = 0;
      if (elapsed.includes('m')) {
        const match = elapsed.match(/(\d+)m\s*(\d+)s/);
        if (match)
          elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
      } else {
        elapsedSeconds = parseInt(elapsed.replace('s', ''), 10) || 0;
      }
      startTime.value = Date.now() - elapsedSeconds * 1000;
      clearInterval(elapsedTimeInterval);
      if (data.status === 'running') {
        elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
        pollExistenceProgress();
      }
    } else if (data && data.status === 'finished') {
      // Se o backend já tiver resultado em cache, fetchExistenceCheck buscará o cache
      await fetchExistenceCheck(true);
    }
  } catch (e) {
    // ignore - não bloqueara o componente
    error.value =
      e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_LOAD_STATE');
  }
});

const CATEGORIES = computed(() => [
  { key: 'image', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.IMAGE') },
  { key: 'video', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.VIDEO') },
  { key: 'audio', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.AUDIO') },
  {
    key: 'documents',
    label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.DOCUMENTS'),
  },
  { key: 'other', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.OTHER') },
]);

const loading = ref(false);
const error = ref(null);
const analysis = ref({
  local: { total_count: 0, total_size: 0, by_type: {} },
  s3: { total_count: 0, total_size: 0, by_type: {} },
});
const hasRun = ref(false);
const migrationRef = ref(null);
const baseAnalysisElapsedTime = ref('0s');
const baseAnalysisStartTime = ref(null);
let baseAnalysisElapsedInterval = null;

function updateBaseAnalysisElapsedTime() {
  if (!baseAnalysisStartTime.value) {
    baseAnalysisElapsedTime.value = '0s';
    return;
  }
  const elapsed = Math.floor((Date.now() - baseAnalysisStartTime.value) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  baseAnalysisElapsedTime.value =
    minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

const fetchAnalysis = async () => {
  loading.value = true;
  error.value = null;
  baseAnalysisStartTime.value = Date.now();
  baseAnalysisElapsedTime.value = '0s';
  clearInterval(baseAnalysisElapsedInterval);
  baseAnalysisElapsedInterval = setInterval(
    updateBaseAnalysisElapsedTime,
    1000
  );
  const start = Date.now();
  let success = false;
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_analysis`
    );
    // Garante que todas as categorias existam, mesmo que zeradas
    ['local', 's3'].forEach(storageKey => {
      if (!data[storageKey].by_type) data[storageKey].by_type = {};
      CATEGORIES.value.forEach(cat => {
        if (!data[storageKey].by_type[cat.key]) {
          data[storageKey].by_type[cat.key] = { count: 0, size: 0 };
        }
      });
    });
    analysis.value = data;
    // mark success; will set hasRun after minDelay finishes
    success = true;
  } catch (e) {
    error.value = e?.response?.data?.message || 'Failed to load analysis.';
  } finally {
    const elapsed = Date.now() - start;
    const minDelay = 3000;
    // eslint-disable-next-line no-promise-executor-return
    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
    if (elapsed < minDelay) {
      await sleep(minDelay - elapsed);
    }
    loading.value = false;
    clearInterval(baseAnalysisElapsedInterval);
    baseAnalysisElapsedInterval = null;
    updateBaseAnalysisElapsedTime();
    baseAnalysisStartTime.value = null;
    if (success) {
      hasRun.value = true;
      try {
        if (
          migrationRef.value &&
          typeof migrationRef.value.refreshState === 'function'
        ) {
          await migrationRef.value.refreshState();
        }
      } catch (err) {
        error.value =
          err?.message ||
          t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_REFRESH_MIGRATION');
      }
      // Notify sibling components that an analysis has just completed so they can update UI
      try {
        window.dispatchEvent(
          new CustomEvent('storage_analysis:done', {
            detail: {
              inboxId: props.inboxId,
              hasLocalFiles: hasLocalFiles.value,
              bucket: props.bucket,
            },
          })
        );
      } catch (e) {
        // ignore - best-effort
      }
    }
  }
};

const formatSize = size => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024)
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const isLocalStorageSelected = computed(() => props.storageType === 'local');
const showS3Blocks = computed(() => !isLocalStorageSelected.value);
const showLocalFolderStyleColumns = computed(
  () => props.folderStyleEnabled && isLocalStorageSelected.value
);
const showStorageMigrationSection = computed(() => {
  return (
    !isLocalStorageSelected.value &&
    hasRun.value &&
    !loading.value &&
    (hasLocalFiles.value || existenceResult.value)
  );
});

// Computed totals based on selected storage mode
const totalCount = computed(() => {
  const localCount = Number(analysis.value.local?.total_count || 0);
  const s3Count = Number(analysis.value.s3?.total_count || 0);
  if (isLocalStorageSelected.value) return localCount;
  return localCount + s3Count;
});

const totalSize = computed(() => {
  const localSize = Number(analysis.value.local?.total_size || 0);
  const s3Size = Number(analysis.value.s3?.total_size || 0);
  if (isLocalStorageSelected.value) return localSize;
  return localSize + s3Size;
});

// Indicates whether there are files for the selected storage mode.
const hasFiles = computed(() => {
  const localCount = Number(analysis.value.local?.total_count || 0);
  const s3Count = Number(analysis.value.s3?.total_count || 0);
  if (isLocalStorageSelected.value) return localCount > 0;
  return localCount + s3Count > 0;
});

// Indica se há arquivos apenas no Local (usado para exibir a seção de Migração)
const hasLocalFiles = computed(() => {
  const localCount = Number(analysis.value.local?.total_count || 0);
  return localCount > 0;
});

// O botão de iniciar migração deve estar habilitado somente quando houver cache de existência (existenceResult)
// e existirem arquivos locais.
const canStartMigration = computed(() => {
  return !!existenceResult.value && hasLocalFiles.value;
});

const folderStyleCategoryCountersFor = (storageKey, existsFlag) => {
  const counters = {};
  CATEGORIES.value.forEach(cat => {
    counters[cat.key] = { old: 0, new: 0 };
  });

  const details = existenceResult.value?.[storageKey]?.details || [];
  const filtered = details.filter(d => Boolean(d?.exists) === existsFlag);

  if (filtered.length > 0) {
    filtered.forEach(item => {
      const category = item?.category || 'other';
      if (!counters[category]) counters[category] = { old: 0, new: 0 };
      if (item?.folder_style === 'new_style') {
        counters[category].new += 1;
      } else {
        counters[category].old += 1;
      }
    });
    return counters;
  }

  // Fallback when details are not available: consider all as old style.
  const summaryKey = existsFlag ? 'found' : 'orphans';
  const summary =
    existenceResult.value?.[storageKey]?.summary?.[summaryKey] || {};
  CATEGORIES.value.forEach(cat => {
    counters[cat.key].old = Number(summary[cat.key]?.count || 0);
  });

  return counters;
};

const foundLocalFolderStyleCategoryCounters = computed(() =>
  folderStyleCategoryCountersFor('local', true)
);

const missingLocalFolderStyleCategoryCounters = computed(() =>
  folderStyleCategoryCountersFor('local', false)
);

const foundS3FolderStyleCategoryCounters = computed(() =>
  folderStyleCategoryCountersFor('s3', true)
);

const missingS3FolderStyleCategoryCounters = computed(() =>
  folderStyleCategoryCountersFor('s3', false)
);

// Removido o fetchAnalysis automático. O usuário deve clicar no botão para analisar.
const elapsedTime = ref('0s');

function updateElapsedTime() {
  if (!startTime.value || existenceProgress.value.status === 'not_started') {
    elapsedTime.value = '0s';
    return;
  }
  const now = Date.now();
  const elapsed = Math.floor((now - startTime.value) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  elapsedTime.value = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

let elapsedTimeInterval = null;

const pauseExistenceCheck = async () => {
  try {
    // Salva o estado pausado no backend (Redis)
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/pause`,
      {
        processed: existenceProgress.value.processed,
        total: existenceProgress.value.total,
        elapsedTime: elapsedTime.value,
        is_refetch: !!isRefetchClicked.value,
      }
    );
    existenceProgress.value.status = 'paused';
    clearInterval(progressInterval);
    clearInterval(elapsedTimeInterval);
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao pausar o processo.';
  }
};

const resumeExistenceCheck = async () => {
  try {
    // Recupera o estado do backend e retoma o progresso
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/resume`
    );
    // Busca o estado atualizado, incluindo o tempo salvo
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/state`
    );
    existenceProgress.value = data;

    // Restore isRefetchClicked if backend persisted it; otherwise try to infer
    if (typeof data.is_refetch !== 'undefined') {
      isRefetchClicked.value = !!data.is_refetch;
    } else {
      try {
        await fetchExistenceCheck(true);
        isRefetchClicked.value = !!existenceResult.value;
      } catch (e) {
        // ignore
      }
    }

    const elapsed = data.elapsed_time || '0s';
    elapsedTime.value = elapsed;
    let elapsedSeconds = 0;
    if (elapsed.includes('m')) {
      const match = elapsed.match(/(\d+)m\s*(\d+)s/);
      if (match) {
        elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
      }
    } else {
      elapsedSeconds = parseInt(elapsed.replace('s', ''), 10) || 0;
    }
    startTime.value = Date.now() - elapsedSeconds * 1000;
    clearInterval(elapsedTimeInterval);
    // Só inicia o timer se o status retornado for running
    if (data.status === 'running') {
      elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
    }
    pollExistenceProgress();
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao retomar o processo.';
  }
};

const stopExistenceCheck = async () => {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/stop`
    );
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Falha ao interromper o processo.';
  } finally {
    // Limpa imediatamente o estado local
    existenceProgress.value = { status: 'not_started', processed: 0, total: 0 };
    existenceResult.value = null;
    isRefetchClicked.value = false;
    loadingExistence.value = false;
    clearInterval(progressInterval);
    clearInterval(elapsedTimeInterval);
    elapsedTime.value = '0s';
    // Broadcast the stop so other components (e.g., FolderStyleMigration) can re-enable controls
    try {
      window.dispatchEvent(
        new CustomEvent('storage_existence:status', {
          detail: { inboxId: props.inboxId, status: 'not_started' },
        })
      );
    } catch (e) {
      // non-blocking
      error.value =
        e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_DISPATCH_EVENT');
    }

    // Ask the migration component to refresh its state so its session is visible if present
    try {
      if (
        migrationRef.value &&
        typeof migrationRef.value.refreshState === 'function'
      ) {
        migrationRef.value.refreshState();
      }
    } catch (e) {
      error.value =
        e?.message || t('INBOX_MGMT.STORAGE_ANALYSIS.ERROR_REFRESH_MIGRATION');
    }
  }
};

const restartExistenceCheck = async () => {
  try {
    // Limpa estado local e qualquer cache local antes de reiniciar
    clearInterval(progressInterval);
    clearInterval(elapsedTimeInterval);
    existenceResult.value = null;
    analysisDate.value = null;
    elapsedTime.value = '0s';
    existenceProgress.value = { status: 'not_started', processed: 0, total: 0 };
    isRefetchClicked.value = false;

    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/restart`
    );
    // Após reiniciar no backend, dispara o fluxo de início
    startExistenceCheck();
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Falha ao reiniciar o processo.';
  }
};

onMounted(async () => {
  try {
    // Recupera o estado do progresso do backend (Redis)
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_existence_checks/${props.inboxId}/state`
    );
    existenceProgress.value = data;

    // Usa o campo correto do backend e restaura o timer
    if (['paused', 'running', 'finished'].includes(data.status)) {
      const elapsed = data.elapsed_time || '0s';
      elapsedTime.value = elapsed;
      let elapsedSeconds = 0;
      if (elapsed.includes('m')) {
        const match = elapsed.match(/(\d+)m\s*(\d+)s/);
        if (match) {
          elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        }
      } else {
        elapsedSeconds = parseInt(elapsed.replace('s', ''), 10) || 0;
      }
      startTime.value = Date.now() - elapsedSeconds * 1000;
      clearInterval(elapsedTimeInterval);
      // Só inicia o timer se estiver running
      if (data.status === 'running') {
        elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
      }
      // Restore isRefetchClicked from backend state if it's provided by the server
      if (typeof data.is_refetch !== 'undefined') {
        isRefetchClicked.value = !!data.is_refetch;
      } else if (['running', 'paused'].includes(data.status)) {
        // If not provided, try to infer: fetch last existenceResult silently and set flag if present
        try {
          await fetchExistenceCheck(true);
          isRefetchClicked.value = !!existenceResult.value;
        } catch (e) {
          // ignore - best effort
        }
      }
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Falha ao carregar o estado do processo.';
  }
});

const toggleShowLastExecution = async () => {
  showLastExecution.value = !showLastExecution.value;
  if (showLastExecution.value) {
    // When showing last execution, fetch cached report silently without starting polling
    await fetchExistenceCheck(true);
  }
};
</script>

<template>
  <div>
    <div class="grid grid-cols-3 items-center mb-2">
      <div class="flex items-center gap-2 justify-start">
        <div>
          <h3 class="text-lg font-semibold text-n-brand">
            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1 whitespace-nowrap">
            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <div />
      <div class="flex items-center justify-end">
        <button
          :disabled="loading"
          class="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0 text-black"
          :class="[hasRun ? 'bg-yellow-500' : 'bg-n-brand']"
          @click="fetchAnalysis"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          <span class="whitespace-nowrap">
            {{
              hasRun
                ? t('INBOX_MGMT.STORAGE_ANALYSIS.RERUN')
                : t('INBOX_MGMT.STORAGE_ANALYSIS.RUN')
            }}
          </span>
        </button>
      </div>
    </div>
    <div
      v-if="loading || hasRun"
      class="text-center text-sm text-slate-700 dark:text-slate-400 mb-3"
    >
      {{ t('INBOX_MGMT.STORAGE_ANALYSIS.ELAPSED_TIME') }}:
      {{ baseAnalysisElapsedTime }}
    </div>
    <div v-if="hasRun">
      <div v-if="error" class="text-red-600">{{ error }}</div>
      <div v-else>
        <!-- Resultado do Chatwoot blobs analyse -->
        <div class="ml-0 mr-0 py-8 w-full">
          <!-- Bloco total combinando Local + S3 -->
          <div
            class="rounded-lg p-2 mb-3 bg-white dark:bg-[#23262b] border border-slate-200 dark:border-[#35373c] flex flex-col items-center text-center w-40 mx-auto"
          >
            <div class="text-base text-slate-900 dark:text-white font-medium">
              {{ t('INBOX_MGMT.STORAGE_ANALYSIS.TOTAL') }}
            </div>
            <div class="mt-1">
              <span
                v-if="loading"
                class="inline-block h-10 w-20 bg-slate-700 rounded animate-pulse"
              />
              <div v-else class="text-4xl font-bold text-n-brand">
                {{ totalCount }}
              </div>
            </div>
            <div class="mt-1">
              <span
                v-if="loading"
                class="inline-block h-7 w-16 bg-slate-700 rounded animate-pulse"
              />
              <div v-else class="text-2xl font-bold text-green-400">
                {{ formatSize(totalSize) }}
              </div>
            </div>
          </div>
          <div
            class="flex flex-col md:flex-row md:gap-3 justify-center items-center"
          >
            <!-- Local Storage Block -->
            <div
              class="bg-white dark:bg-[#23262b] border border-slate-200 dark:border-[#35373c] rounded-lg p-6 min-w-[320px] max-w-md flex-1 flex flex-col justify-between m-0"
            >
              <div class="flex items-center justify-between mb-2">
                <span
                  class="text-xs text-slate-600 dark:text-slate-400 font-medium"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.TOTAL_SIZE') }}</span
                >
                <span
                  class="text-lg text-slate-900 dark:text-white font-medium"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.LOCAL') }}</span
                >
              </div>
              <div class="flex items-center justify-between mb-2">
                <span
                  v-if="loading"
                  class="h-7 w-16 bg-slate-700 rounded animate-pulse"
                />
                <span v-else class="text-2xl font-bold text-green-400">{{
                  formatSize(analysis.local.total_size)
                }}</span>
                <span
                  v-if="loading"
                  class="h-8 w-20 bg-slate-700 rounded animate-pulse"
                />
                <span v-else class="text-4xl font-bold text-n-brand">{{
                  analysis.local.total_count
                }}</span>
              </div>
              <div class="flex items-center justify-between mb-1 mt-4">
                <span />
                <span class="text-xs text-slate-400 font-semibold text-right">{{
                  t('INBOX_MGMT.STORAGE_ANALYSIS.FILE_TYPES')
                }}</span>
              </div>
              <div>
                <table
                  class="w-full text-xs text-slate-700 dark:text-slate-200"
                >
                  <tbody>
                    <tr
                      v-for="cat in CATEGORIES"
                      :key="cat.key"
                      class="border-none"
                    >
                      <td class="py-0.5 pl-0 text-left font-semibold">
                        <span
                          v-if="loading"
                          class="inline-block h-4 w-8 bg-slate-700 rounded animate-pulse"
                        />
                        <template v-else>
                          <span class="text-slate-900 dark:text-white">{{
                            analysis.local.by_type?.[cat.key]?.count || 0
                          }}</span>
                          <span
                            class="text-slate-500 dark:text-slate-400 font-normal"
                          >
                            ({{
                              formatSize(
                                analysis.local.by_type?.[cat.key]?.size || 0
                              )
                            }})</span
                          >
                        </template>
                      </td>
                      <td
                        class="py-0.5 pr-0 text-slate-600 dark:text-slate-300 font-mono lowercase text-right"
                      >
                        {{ cat.label }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- S3 Storage Block -->
            <div
              v-if="showS3Blocks"
              class="bg-white dark:bg-[#23262b] border border-slate-200 dark:border-[#35373c] rounded-lg p-6 min-w-[320px] max-w-md flex-1 flex flex-col justify-between m-0"
            >
              <div class="flex items-center justify-between mb-2">
                <span
                  class="text-lg text-slate-900 dark:text-white font-medium"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.S3_STORAGE') }}</span
                >
                <span
                  class="text-xs text-slate-600 dark:text-slate-400 font-medium"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.TOTAL_SIZE') }}</span
                >
              </div>
              <div class="flex items-center justify-between mb-2">
                <span
                  v-if="loading"
                  class="h-8 w-20 bg-slate-700 rounded animate-pulse"
                />
                <span v-else class="text-4xl font-bold text-n-brand">{{
                  analysis.s3.total_count
                }}</span>
                <span
                  v-if="loading"
                  class="h-7 w-16 bg-slate-700 rounded animate-pulse"
                />
                <span v-else class="text-2xl font-bold text-green-400">{{
                  formatSize(analysis.s3.total_size)
                }}</span>
              </div>
              <div class="text-xs text-slate-400 font-semibold mb-1 mt-4">
                {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILE_TYPES') }}
              </div>
              <div>
                <table
                  class="w-full text-xs text-slate-700 dark:text-slate-200"
                >
                  <tbody>
                    <tr
                      v-for="cat in CATEGORIES"
                      :key="cat.key"
                      class="border-none"
                    >
                      <td
                        class="py-0.5 pl-0 text-slate-600 dark:text-slate-300 font-mono lowercase"
                      >
                        {{ cat.label }}
                      </td>
                      <td class="py-0.5 pr-0 text-right font-semibold">
                        <span
                          v-if="loading"
                          class="inline-block h-4 w-8 bg-slate-700 rounded animate-pulse"
                        />
                        <template v-else>
                          <span
                            class="text-slate-500 dark:text-slate-400 font-normal"
                            >({{
                              formatSize(
                                analysis.s3.by_type?.[cat.key]?.size || 0
                              )
                            }})</span
                          >
                          <span class="ml-2 text-slate-900 dark:text-white">{{
                            analysis.s3.by_type?.[cat.key]?.count || 0
                          }}</span>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <!-- Analise detalhada -->
        <div
          v-if="hasRun && !loading && hasFiles"
          class="ml-0 mr-0 py-8 w-full border-t border-solid border-n-weak/60 dark:border-n-weak"
        >
          <div class="grid grid-cols-3 items-center mb-2">
            <div class="flex items-center gap-2 justify-start">
              <h3 class="text-lg font-semibold text-n-brand">
                {{ t('INBOX_MGMT.STORAGE_ANALYSIS.DETAILED_ANALYSIS') }}
              </h3>
            </div>
            <div class="flex justify-center">
              <button
                class="inline-flex items-center gap-2 whitespace-nowrap px-3 py-1 rounded bg-slate-700 text-white text-sm font-medium hover:brightness-110"
                :aria-pressed="!showLastExecution"
                @click="toggleShowLastExecution"
              >
                {{
                  showLastExecution
                    ? isRefetchClicked
                      ? t('INBOX_MGMT.STORAGE_ANALYSIS.HIDE_EXECUTION')
                      : t('INBOX_MGMT.STORAGE_ANALYSIS.HIDE_LAST_EXECUTION')
                    : isRefetchClicked
                      ? t('INBOX_MGMT.STORAGE_ANALYSIS.SHOW_EXECUTION')
                      : t('INBOX_MGMT.STORAGE_ANALYSIS.SHOW_LAST_EXECUTION')
                }}
              </button>
            </div>
            <div class="flex items-center justify-end">
              <button
                :disabled="
                  loadingExistence ||
                  ['running', 'paused'].includes(existenceProgress.status) ||
                  ['running', 'paused'].includes(props.folderStyleStatus) ||
                  ['running', 'paused'].includes(props.storageMigrationStatus)
                "
                :title="existenceButtonTitle"
                :class="
                  existenceResult
                    ? 'inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded bg-yellow-500 text-black text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0'
                    : 'inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0'
                "
                :style="existenceResult ? { color: 'rgb(0,0,0)' } : {}"
                @click="startExistenceCheck"
              >
                <svg
                  v-if="
                    loadingExistence || existenceProgress.status === 'running'
                  "
                  class="animate-spin h-4 w-4 mr-1 inline-block"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                <span>{{
                  existenceResult
                    ? t('INBOX_MGMT.STORAGE_ANALYSIS.RERUN_DETAILED_ANALYSIS')
                    : t('INBOX_MGMT.STORAGE_ANALYSIS.START_DETAILED_ANALYSIS')
                }}</span>
              </button>
            </div>
          </div>
          <div
            v-if="
              showLastExecution &&
              ['running', 'paused', 'finished'].includes(
                existenceProgress.status
              )
            "
          >
            <div
              class="w-full bg-slate-700 rounded h-5 mb-1 relative overflow-hidden"
            >
              <div
                class="h-5 rounded transition-all duration-700"
                :class="
                  existenceProgress.status === 'finished'
                    ? 'bg-green-500'
                    : 'bg-n-brand'
                "
                :style="{
                  width:
                    (existenceProgress.processed / existenceProgress.total) *
                      100 +
                    '%',
                }"
              />
              <span
                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold px-2 pointer-events-none"
                :class="
                  existenceProgress.status === 'finished'
                    ? 'text-green-900'
                    : 'text-white'
                "
              >
                {{ percentProgress }}
              </span>
            </div>
            <!-- Linha com data da análise (centro) e status concluído (direita) -->
            <div class="grid grid-cols-3 items-center mb-1">
              <div />
              <div
                class="text-center text-xs text-slate-700 dark:text-slate-400"
              >
                <span v-if="analysisDate"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.LAST_ANALYSIS') }}:
                  {{ analysisDate }}</span
                >
              </div>
              <div class="text-right text-xs">
                <span
                  v-if="existenceProgress.status === 'finished'"
                  class="text-green-400 font-semibold"
                  >✔️ {{ t('INBOX_MGMT.STORAGE_ANALYSIS.COMPLETED') }}</span
                >
              </div>
            </div>
            <!-- Timer abaixo da linha de data/status -->
            <div
              v-if="
                ['running', 'paused', 'finished'].includes(
                  existenceProgress.status
                )
              "
              class="text-center text-sm text-slate-700 dark:text-slate-400 mt-2"
            >
              {{ t('INBOX_MGMT.STORAGE_ANALYSIS.ELAPSED_TIME') }}:
              {{ elapsedTime }}
            </div>
            <div class="flex justify-between mt-2">
              <button
                v-if="existenceProgress.status === 'running'"
                class="px-4 py-2 bg-yellow-500 text-black !text-black dark:!text-black rounded hover:brightness-110"
                @click="pauseExistenceCheck"
              >
                <span class="text-black !text-black dark:!text-black">
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.PAUSE') }}
                </span>
              </button>
              <div v-if="existenceProgress.status === 'paused'">
                <button
                  class="px-4 py-2 bg-green-500 text-white rounded hover:brightness-110"
                  @click="resumeExistenceCheck"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESUME') }}
                </button>
                <button
                  class="inline-flex items-center gap-2 px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0 ml-2"
                  @click="restartExistenceCheck"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESTART') }}
                </button>
              </div>
              <button
                v-if="['running', 'paused'].includes(existenceProgress.status)"
                class="px-4 py-2 bg-red-500 text-white rounded hover:brightness-110"
                @click="stopExistenceCheck"
              >
                {{ t('INBOX_MGMT.STORAGE_ANALYSIS.STOP') }}
              </button>
            </div>
          </div>

          <!-- Código removido: cálculo do percentual está apenas no <script setup> -->
          <!-- Exibe o relatório da Analise detalhada após a conclusão -->
          <div
            v-if="
              showLastExecution &&
              existenceResult &&
              existenceProgress.status === 'finished'
            "
            class="mt-4"
          >
            <h5 class="text-base font-semibold text-n-brand mb-2 text-center">
              {{ t('INBOX_MGMT.STORAGE_ANALYSIS.SUMMARY') }}
            </h5>
            <div
              class="grid grid-cols-1 gap-4"
              :class="showS3Blocks ? 'md:grid-cols-2' : 'md:grid-cols-1'"
            >
              <!-- Local -->
              <div
                :class="showS3Blocks ? '' : 'md:max-w-2xl md:mx-auto w-full'"
              >
                <div
                  class="rounded-lg p-4 flex flex-col gap-3 bg-white dark:bg-[rgb(35,38,43)]"
                >
                  <div
                    class="font-semibold text-n-brand mb-2 text-lg text-center"
                  >
                    {{ t('INBOX_MGMT.STORAGE_ANALYSIS.LOCAL') }}
                  </div>
                  <div
                    class="rounded-lg p-3 bg-green-50/80 border border-green-200"
                  >
                    <div
                      class="text-xs text-green-800 font-semibold mb-1 text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FOUND') }}
                    </div>
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="text-slate-600 dark:text-slate-8000">
                          <th class="text-left py-0.5">Tipo</th>
                          <th
                            v-if="showLocalFolderStyleColumns"
                            class="text-center py-0.5"
                          >
                            OLD
                          </th>
                          <th
                            v-if="showLocalFolderStyleColumns"
                            class="text-center py-0.5"
                          >
                            NEW
                          </th>
                          <th class="text-right py-0.5">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="cat in CATEGORIES"
                          :key="cat.key"
                          class="border-none"
                        >
                          <td
                            class="py-0.5 font-semibold text-slate-900 dark:text-white"
                          >
                            {{ cat.label }}
                          </td>
                          <td
                            v-if="showLocalFolderStyleColumns"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              foundLocalFolderStyleCategoryCounters[cat.key]
                                ?.old || 0
                            }}
                          </td>
                          <td
                            v-if="showLocalFolderStyleColumns"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              foundLocalFolderStyleCategoryCounters[cat.key]
                                ?.new || 0
                            }}
                          </td>
                          <td
                            class="py-0.5 text-right font-semibold text-green-700"
                          >
                            {{
                              existenceResult.local.summary.found[cat.key]
                                ?.count || 0
                            }}
                            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }} ({{
                              formatSize(
                                existenceResult.local.summary.found[cat.key]
                                  ?.size || 0
                              )
                            }})
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    class="rounded-lg p-3 bg-red-50/80 border border-red-200"
                  >
                    <div
                      class="text-xs text-red-800 font-semibold mb-1 text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_ANALYSIS.MISSING') }}
                    </div>
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="text-slate-600 dark:text-slate-8000">
                          <th class="text-left py-0.5">Tipo</th>
                          <th
                            v-if="showLocalFolderStyleColumns"
                            class="text-center py-0.5"
                          >
                            OLD
                          </th>
                          <th
                            v-if="showLocalFolderStyleColumns"
                            class="text-center py-0.5"
                          >
                            NEW
                          </th>
                          <th class="text-right py-0.5">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="cat in CATEGORIES"
                          :key="cat.key + '-orphan'"
                          class="border-none"
                        >
                          <td
                            class="py-0.5 font-semibold text-slate-900 dark:text-white"
                          >
                            {{ cat.label }}
                          </td>
                          <td
                            v-if="showLocalFolderStyleColumns"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              missingLocalFolderStyleCategoryCounters[cat.key]
                                ?.old || 0
                            }}
                          </td>
                          <td
                            v-if="showLocalFolderStyleColumns"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              missingLocalFolderStyleCategoryCounters[cat.key]
                                ?.new || 0
                            }}
                          </td>
                          <td
                            class="py-0.5 text-right font-semibold text-red-700"
                          >
                            {{
                              existenceResult.local.summary.orphans[cat.key]
                                ?.count || 0
                            }}
                            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }} ({{
                              formatSize(
                                existenceResult.local.summary.orphans[cat.key]
                                  ?.size || 0
                              )
                            }})
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- S3 -->
              <div v-if="showS3Blocks">
                <div
                  class="rounded-lg p-4 flex flex-col gap-3 bg-white dark:bg-[rgb(35,38,43)]"
                >
                  <div
                    class="font-semibold text-n-brand mb-2 text-lg text-center"
                  >
                    {{ t('INBOX_MGMT.STORAGE_ANALYSIS.S3_STORAGE') }}
                  </div>
                  <div
                    class="rounded-lg p-3 bg-green-50/80 border border-green-200"
                  >
                    <div
                      class="text-xs text-green-800 font-semibold mb-1 text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FOUND') }}
                    </div>
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="text-slate-600 dark:text-slate-8000">
                          <th class="text-left py-0.5">Tipo</th>
                          <th
                            v-if="props.folderStyleEnabled"
                            class="text-center py-0.5"
                          >
                            OLD
                          </th>
                          <th
                            v-if="props.folderStyleEnabled"
                            class="text-center py-0.5"
                          >
                            NEW
                          </th>
                          <th class="text-right py-0.5">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="cat in CATEGORIES"
                          :key="cat.key"
                          class="border-none"
                        >
                          <td
                            class="py-0.5 font-semibold text-slate-900 dark:text-white"
                          >
                            {{ cat.label }}
                          </td>
                          <td
                            v-if="props.folderStyleEnabled"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              foundS3FolderStyleCategoryCounters[cat.key]
                                ?.old || 0
                            }}
                          </td>
                          <td
                            v-if="props.folderStyleEnabled"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              foundS3FolderStyleCategoryCounters[cat.key]
                                ?.new || 0
                            }}
                          </td>
                          <td
                            class="py-0.5 text-right font-semibold text-green-700"
                          >
                            {{
                              existenceResult.s3.summary.found[cat.key]
                                ?.count || 0
                            }}
                            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }} ({{
                              formatSize(
                                existenceResult.s3.summary.found[cat.key]
                                  ?.size || 0
                              )
                            }})
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    class="rounded-lg p-3 bg-red-50/80 border border-red-200"
                  >
                    <div
                      class="text-xs text-red-800 font-semibold mb-1 text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_ANALYSIS.MISSING') }}
                    </div>
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="text-slate-600 dark:text-slate-8000">
                          <th class="text-left py-0.5">Tipo</th>
                          <th
                            v-if="props.folderStyleEnabled"
                            class="text-center py-0.5"
                          >
                            OLD
                          </th>
                          <th
                            v-if="props.folderStyleEnabled"
                            class="text-center py-0.5"
                          >
                            NEW
                          </th>
                          <th class="text-right py-0.5">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="cat in CATEGORIES"
                          :key="cat.key + '-orphan'"
                          class="border-none"
                        >
                          <td
                            class="py-0.5 font-semibold text-slate-900 dark:text-white"
                          >
                            {{ cat.label }}
                          </td>
                          <td
                            v-if="props.folderStyleEnabled"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              missingS3FolderStyleCategoryCounters[cat.key]
                                ?.old || 0
                            }}
                          </td>
                          <td
                            v-if="props.folderStyleEnabled"
                            class="py-0.5 text-center font-semibold text-slate-500"
                          >
                            {{
                              missingS3FolderStyleCategoryCounters[cat.key]
                                ?.new || 0
                            }}
                          </td>
                          <td
                            class="py-0.5 text-right font-semibold text-red-700"
                          >
                            {{
                              existenceResult.s3.summary.orphans[cat.key]
                                ?.count || 0
                            }}
                            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }} ({{
                              formatSize(
                                existenceResult.s3.summary.orphans[cat.key]
                                  ?.size || 0
                              )
                            }})
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Migração para S3 (após Analise detalhada) -->
        <div
          v-if="showStorageMigrationSection"
          class="ml-0 mr-0 pt-8 pb-4 w-full border-t border-solid border-n-weak/60 dark:border-n-weak"
        >
          <StorageMigration
            ref="migrationRef"
            :inbox-id="props.inboxId"
            :existence-status="existenceProgress.status"
            :folder-style-status="props.folderStyleStatus"
            :can-start-migration="canStartMigration"
            :has-local-files="hasLocalFiles"
          />
        </div>
      </div>
    </div>
  </div>
</template>
